import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface LocationAddress {
  formatted: string;
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
}

// Helper para parsear address_components do Google
function parseGoogleAddressComponents(components: any[]): LocationAddress {
  const getComponent = (type: string, useShortName = false) => {
    const component = components?.find(c => c.types.includes(type));
    return useShortName ? (component?.short_name || '') : (component?.long_name || '');
  };
  
  const street = getComponent('route');
  const number = getComponent('street_number');
  const neighborhood = getComponent('sublocality_level_1') || 
                       getComponent('neighborhood');
  const city = getComponent('administrative_area_level_2') || 
               getComponent('locality');
  
  // Usar short_name para estado (retorna "SP", "RJ", etc.)
  const state = getComponent('administrative_area_level_1', true);
  
  // FORMATO COMPACTO: Rua, Número - Cidade - UF
  const streetPart = street && number ? `${street}, ${number}` : street;
  
  const formatted = [
    streetPart,       // "Rua Brasil, 100" ou "Rua Brasil" (se não tiver número)
    city,             // "São Paulo"
    state             // "SP"
  ].filter(Boolean).join(' - ');
  
  return {
    formatted: formatted || 'Endereço não disponível',
    street,
    number,
    neighborhood,
    city,
    state
  };
}

export const useLocationAddress = (
  companyId: string | undefined,
  latitude?: number,
  longitude?: number
) => {
  return useQuery({
    queryKey: ['location-address', latitude, longitude],
    queryFn: async (): Promise<LocationAddress | null> => {
      if (!latitude || !longitude) return null;

      try {
        console.log('📍 Buscando endereço (cache-first):', { latitude, longitude });
        
        // 1️⃣ BUSCAR NO CACHE PRIMEIRO (raio 100m)
        const { data: cachedAddress, error: cacheError } = await (supabase as any).rpc(
          'get_cached_geocoded_address',
          { 
            p_lat: latitude, 
            p_lng: longitude,
            p_radius_meters: 100 
          }
        );

        if (!cacheError && cachedAddress && Array.isArray(cachedAddress) && cachedAddress.length > 0) {
          const cached = cachedAddress[0];
          console.log('✅ Cache HIT! Distância:', cached.distance_meters, 'm');
          
          // Recomputar formato compacto mesmo em cache hits
          const streetPart = cached.street && cached.number 
            ? `${cached.street}, ${cached.number}` 
            : cached.street;

          const formatted = [streetPart, cached.city, cached.state]
            .filter(Boolean)
            .join(' - ') || 'Endereço não disponível';
          
          return {
            formatted,
            street: cached.street || '',
            number: cached.number || '',
            neighborhood: cached.neighborhood || '',
            city: cached.city || '',
            state: cached.state || ''
          };
        }

        console.log('⚠️ Cache MISS - Chamando Google Geocoding API');
        
        // 2️⃣ CACHE MISS - Buscar via Google Geocoding API
        const { data: geocodeData, error: geocodeError } = await supabase.functions.invoke(
          'google-geocode',
          { body: { lat: latitude, lng: longitude } }
        );

        if (geocodeError) {
          console.error('❌ Geocoding error:', geocodeError);
          throw geocodeError;
        }

        // Parse resposta do Google
        if (geocodeData?.address_components) {
          const parsedAddress = parseGoogleAddressComponents(geocodeData.address_components);
          
          // 3️⃣ SALVAR NO CACHE ASSINCRONAMENTE (fire-and-forget)
          void (async () => {
            try {
              await (supabase as any).rpc('upsert_geocoded_address', {
                p_lat: latitude,
                p_lng: longitude,
                p_formatted_address: parsedAddress.formatted,
                p_street: parsedAddress.street,
                p_number: parsedAddress.number,
                p_neighborhood: parsedAddress.neighborhood,
                p_city: parsedAddress.city,
                p_state: parsedAddress.state,
                p_place_id: geocodeData.place_id || null
              });
              console.log('💾 Endereço salvo no cache');
            } catch (err) {
              console.warn('⚠️ Erro ao salvar no cache:', err);
            }
          })();
          
          return parsedAddress;
        }

        console.warn('⚠️ No address in response, using fallback');
        return { 
          formatted: 'Endereço não disponível', 
          street: '', 
          number: '', 
          neighborhood: '', 
          city: '', 
          state: '' 
        };
      } catch (error) {
        console.error('❌ Exception in geocoding:', error);
        return { 
          formatted: 'Erro ao buscar endereço', 
          street: '', 
          number: '', 
          neighborhood: '', 
          city: '', 
          state: '' 
        };
      }
    },
    enabled: !!latitude && !!longitude,
    staleTime: 1000 * 60 * 60 * 24 * 7, // Cache 7 dias (endereços não mudam)
    gcTime: 1000 * 60 * 60 * 24 * 7, // Manter em memória 7 dias
    retry: 2,
    retryDelay: 1000,
  });
};
